import { useState, useRef, useEffect } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { MessageSquare, User, Send, Settings, Pin, PinOff, LogOut, KeyRound, Star, BarChart3 } from 'lucide-react'
import { getInitials, getAvatarColor } from '@/lib/displayUtils'
import { useGlobalInboxNotifications } from '@/hooks/useGlobalInboxNotifications'
import type { Agent } from '@/types/shared'

interface NavigationRailProps {
  currentAgent: Agent
  activePage: 'inbox' | 'contacts' | 'outbound' | 'ratings' | 'reports' | 'settings'
}

export default function NavigationRail({ currentAgent, activePage }: NavigationRailProps) {
  const { currentAgentTeamIds = [] } = usePage<{ currentAgentTeamIds?: number[] }>().props
  useGlobalInboxNotifications({ enabled: activePage !== 'inbox', currentAgentId: currentAgent.id, currentAgentTeamIds })
  const [isExpanded, setIsExpanded] = useState(false)
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('navPinned') === 'true'
    }
    return false
  })
  const navRef = useRef<HTMLElement>(null)
  const openTimeoutRef = useRef<number | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    localStorage.setItem('navPinned', isPinned.toString())
  }, [isPinned])

  // Check if cursor is over nav on mount (for page navigation)
  useEffect(() => {
    const nav = navRef.current
    if (!nav || isPinned) return

    const checkHover = () => {
      const rect = nav.getBoundingClientRect()
      const mouseX = (window as unknown as { mouseX?: number }).mouseX ?? -1
      const mouseY = (window as unknown as { mouseY?: number }).mouseY ?? -1
      if (
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom
      ) {
        setIsExpanded(true)
      }
    }

    // Track mouse position globally
    const handleMouseMove = (e: MouseEvent) => {
      (window as unknown as { mouseX: number }).mouseX = e.clientX;
      (window as unknown as { mouseY: number }).mouseY = e.clientY
    }
    window.addEventListener('mousemove', handleMouseMove)

    // Check after a small delay to allow for position tracking
    const timeout = setTimeout(checkHover, 50)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(timeout)
    }
  }, [isPinned, activePage])

  const handleMouseEnter = () => {
    if (isPinned) return
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    openTimeoutRef.current = window.setTimeout(() => {
      setIsExpanded(true)
    }, 200)
  }

  const handleMouseLeave = () => {
    if (isPinned) return
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsExpanded(false)
    }, 300)
  }

  const togglePin = () => {
    setIsPinned(!isPinned)
    setIsExpanded(!isPinned)
  }

  const isOpen = isPinned || isExpanded

  const navItems = [
    { href: '/inbox', icon: MessageSquare, label: 'Caixa de Entrada', key: 'inbox' as const },
    { href: '/contacts/users', icon: User, label: 'Contatos', key: 'contacts' as const },
    { href: '/outbound_messages', icon: Send, label: 'Envios', key: 'outbound' as const },
    ...(currentAgent.role === 'admin'
      ? [
          { href: '/ratings', icon: Star, label: 'Avaliações', key: 'ratings' as const },
          { href: '/reports', icon: BarChart3, label: 'Relatórios', key: 'reports' as const },
        ]
      : []),
  ]

  return (
    <div className="relative shrink-0">
      {/* Spacer to maintain layout */}
      <div className={`h-full transition-all duration-200 ${isPinned ? 'w-[195px]' : 'w-[60px]'}`} />

      {/* Actual navigation */}
      <nav
        ref={navRef}
        className={`absolute top-0 left-0 h-full bg-gray-900 flex flex-col p-3 gap-1 transition-all duration-200 ease-in-out z-50 ${
          isOpen ? 'w-[195px]' : 'w-[60px]'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Pin button */}
        <button
          onClick={togglePin}
          className={`absolute top-3 right-3 p-1.5 rounded-lg transition-all duration-200 cursor-pointer text-gray-400 hover:text-white hover:bg-white/10 ${
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          title={isPinned ? 'Desafixar menu' : 'Fixar menu'}
        >
          {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>

        <div className="mt-8">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = activePage === item.key
            return (
              <Link
                key={item.key}
                href={item.href}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span
                  className={`text-sm font-medium whitespace-nowrap overflow-hidden transition-opacity duration-200 ${
                    isOpen ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            )
          })}
        </div>

        {/* Bottom section */}
        <div className="mt-auto pt-4 border-t border-white/10 space-y-1">
          <Link
            href="/settings/profile"
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors ${
              activePage === 'settings'
                ? 'bg-white/10 text-white'
                : 'text-gray-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            <Settings className="w-4 h-4 shrink-0" />
            <span
              className={`text-sm font-medium whitespace-nowrap overflow-hidden transition-opacity duration-200 ${
                isOpen ? 'opacity-100' : 'opacity-0'
              }`}
            >
              Configurações
            </span>
          </Link>
          <div className="relative group mt-2">
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-white/10 overflow-hidden">
              {currentAgent.avatar_url ? (
                <img
                  src={currentAgent.avatar_url}
                  alt={currentAgent.name}
                  className="w-5 h-5 min-w-5 min-h-5 rounded-full object-cover"
                />
              ) : (
                <div
                  className={`w-5 h-5 min-w-5 min-h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium ${getAvatarColor(
                    currentAgent.name
                  )}`}
                >
                  {getInitials(currentAgent.name)}
                </div>
              )}
              <span
                className={`text-sm font-medium text-white whitespace-nowrap overflow-hidden transition-opacity duration-200 ${
                  isOpen ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {currentAgent.name}
              </span>
            </div>
            {/* Dropdown menu on hover */}
            <div className="absolute bottom-0 left-full ml-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
              <div className="bg-gray-800 rounded-lg shadow-lg border border-white/10 py-1 min-w-[140px]">
                <Link
                  href="/settings/profile"
                  className="flex items-center gap-2.5 px-3 py-2 w-full text-gray-300 hover:bg-white/10 hover:text-white"
                >
                  <User className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">Perfil</span>
                </Link>
                <Link
                  href="/settings/password"
                  className="flex items-center gap-2.5 px-3 py-2 w-full text-gray-300 hover:bg-white/10 hover:text-white"
                >
                  <KeyRound className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">Senha</span>
                </Link>
                <div className="border-t border-white/10 my-1" />
                <button
                  onClick={() => router.delete('/logout')}
                  className="flex items-center gap-2.5 px-3 py-2 w-full text-gray-300 hover:bg-white/10 hover:text-white cursor-pointer"
                >
                  <LogOut className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">Sair</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  )
}
